import { useCallback, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API_CONFIG, getApiUrl } from "../config/api";
import {
  OzonReviewsResponse,
  Review,
  StoreConnection,
  WildberriesReviewsResponse,
  YandexReviewRaw,
} from "../types";
import { parseDateToMs } from "../utils/date";

export type PlatformKey = "ozon" | "wildberries" | "yandex";

type OzonCursor = {
  lastId: string | null;
  hasNext: boolean;
  buffer: Review[];
};

type WildberriesCursor = {
  skip: number;
  take: number;
  hasMore: boolean;
  buffer: Review[];
  lastFetched: Review[];
};

type YandexStoreState = { dateTimeTo: string | null; hasMore: boolean };

const PAGE_SIZE = 20;
const DEFAULT_PLATFORM_PAGE_SIZE = 21;

// Placeholder product generator for safety
const createPlaceholderProduct = (
  overrides: Partial<Review["product"]> = {}
): Review["product"] => ({
  created_at: "",
  currency_code: "",
  id: "",
  images: [],
  is_archived: false,
  is_autoarchived: false,
  name: "",
  offer_id: "",
  price: 0,
  primary_image: [],
  sku: "",
  statuses: {
    is_created: false,
    moderate_status: "",
    status: "",
    status_description: "",
    status_failed: "",
    status_name: "",
    status_tooltip: "",
    status_updated_at: "",
    validation_status: "",
  },
  stocks: { has_stock: false, stocks: [] },
  updated_at: "",
  visibility_details: { has_price: false, has_stock: false },
  ...overrides,
});

export const mapOzonToShared = (ozon: unknown): Review => {
  const anyO = ozon as Record<string, unknown>;
  const productInfo = anyO?.product_info as
    | {
        id?: number;
        name?: string;
        offer_id?: string;
        images?: string[];
        primary_image?: string[];
      }
    | undefined;

  const productFromInfo = productInfo
    ? createPlaceholderProduct({
        id: String(productInfo.id ?? ""),
        name: productInfo.name ?? "",
        offer_id: productInfo.offer_id ?? "",
        images: productInfo.images ?? [],
        primary_image: productInfo.primary_image ?? [],
      })
    : undefined;

  const productFinal =
    (anyO as any).product || productFromInfo || createPlaceholderProduct();

  const sellerAnswerText =
    (anyO as any).answer !== undefined && (anyO as any).answer !== null
      ? (anyO as any).answer
      : ((anyO as any).sellerAnswer ?? null);
  const isAnswered =
    typeof sellerAnswerText === "string"
      ? sellerAnswerText.trim().length > 0
      : Boolean(sellerAnswerText);

  const mapped: Review = {
    comments_amount: (anyO as any).comments_amount ?? 0,
    id: String((anyO as any).id ?? ""),
    isAnalyzed: Boolean((anyO as any).isAnalyzed) ?? false,
    isAnsweredBySeller: (anyO as any).isAnsweredBySeller ?? isAnswered,
    isFallback: Boolean((anyO as any).isFallback) ?? false,
    isResponded: Boolean((anyO as any).isResponded) ?? false,
    is_rating_participant:
      Boolean((anyO as any).is_rating_participant) ?? false,
    offer_id: String((anyO as any).offer_id ?? ""),
    order_status: String((anyO as any).order_status ?? ""),
    photos_amount: Number((anyO as any).photos_amount ?? 0),
    platform: "ozon",
    problems: ((anyO as any).problems ?? null) as string | null,
    product: productFinal,
    published_at: ((anyO as any).published_at ||
      (anyO as any).publishedAt ||
      "") as string,
    rating: Number((anyO as any).rating ?? 0),
    recommendations: ((anyO as any).recommendations ?? null) as string | null,
    responseTemplate: ((anyO as any).responseTemplate ?? null) as string | null,
    sellerAnswer: (sellerAnswerText as string | null) ?? null,
    sentiment: ((anyO as any).sentiment ?? null) as string | null,
    sentimentAnalysis: ((anyO as any).sentimentAnalysis ?? null) as
      | string
      | null,
    sku: Number((anyO as any).sku ?? 0),
    status: String((anyO as any).status ?? ""),
    text: String((anyO as any).text ?? ""),
    videos_amount: Number((anyO as any).videos_amount ?? 0),
  };
  if (productInfo) (mapped as any).product_info = productInfo;
  return mapped;
};

export const mapWildberriesToShared = (
  wb: WildberriesReviewsResponse["data"]["feedbacks"][number]
): Review => {
  const textParts = [wb.text, wb.pros, wb.cons].filter(Boolean);
  const productName = wb.productDetails?.productName || "";
  const productOfferId =
    (wb.productDetails?.supplierArticle as any)?.toString?.() || "";
  const productId = (wb.productDetails?.nmId as any)?.toString?.() || "";
  return {
    editable:
      typeof wb.answer?.editable === "boolean" ? wb.answer.editable : undefined,
    comments_amount: 0,
    id: String(wb.id),
    isAnalyzed: (wb as any).isAnalyzed ?? false,
    isAnsweredBySeller: Boolean(wb.answer?.text),
    isFallback: (wb as any).isFallback ?? false,
    isResponded: (wb as any).isResponded ?? Boolean(wb.answer?.text),
    is_rating_participant: false,
    offer_id: productOfferId,
    order_status: wb.state || "",
    photos_amount: wb.photoLinks?.length || 0,
    platform: "wildberries",
    problems: (wb as any).problems ?? null,
    product: createPlaceholderProduct({
      id: productId,
      name: productName,
      offer_id: productOfferId,
    }),
    published_at: wb.createdDate,
    rating: wb.productValuation ?? 0,
    recommendations: (wb as any).recommendations ?? null,
    responseTemplate: (wb as any).responseTemplate ?? null,
    sellerAnswer: wb.answer?.text || null,
    sentiment: (wb as any).sentiment ?? null,
    sentimentAnalysis: (wb as any).sentimentAnalysis ?? null,
    sku: Number(wb.productDetails?.nmId) || 0,
    status: wb.state || "",
    text: textParts.join(" \n"),
    videos_amount: wb.video ? 1 : 0,
    userName: (wb as any).userName || undefined,
  } as Review;
};

export interface UseExternalReviewsResult {
  reviews: Review[];
  loading: boolean;
  hasMore: boolean;
  error: string | null;
  fetchNextPage: () => Promise<void>;
  refresh: () => Promise<void>;
  updateReviews: (updater: React.SetStateAction<Review[]>) => void;
}

export const useExternalReviews = (
  options?: { storeId?: string }
): UseExternalReviewsResult => {
  // Use 20 for per-store pages, 21 for global mixing to aid boundary checks
  const platformPageSize = useMemo(
    () => (options?.storeId ? 20 : DEFAULT_PLATFORM_PAGE_SIZE),
    [options?.storeId]
  );
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filterStoreId = options?.storeId ?? null;

  const authHeaders = useMemo(() => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    } as Record<string, string>;
  }, []);

  const ozonRef = useRef<OzonCursor>({
    lastId: null,
    hasNext: true,
    buffer: [],
  });
  const wbRef = useRef<WildberriesCursor>({
    skip: 0,
    take: platformPageSize,
    hasMore: true,
    buffer: [],
    lastFetched: [],
  });
  const storesRef = useRef<{
    ozon: StoreConnection[];
    wb: StoreConnection[];
    ya: StoreConnection[];
  }>({ ozon: [], wb: [], ya: [] });
  const ozonStoreStateRef = useRef<
    Record<string, { lastId: string | null; hasNext: boolean }>
  >({});
  const wbStoreStateRef = useRef<
    Record<string, { skip: number; take: number; hasMore: boolean }>
  >({});
  const yaStoreStateRef = useRef<Record<string, YandexStoreState>>({});

  const initStoresIfNeeded = useCallback(async () => {
    if (
      storesRef.current.ozon.length ||
      storesRef.current.wb.length ||
      storesRef.current.ya.length
    )
      return;
    const { data: stores } = await axios.get<StoreConnection[]>(
      getApiUrl("/api/stores"),
      { headers: authHeaders }
    );
    const byId = (s: StoreConnection) =>
      !filterStoreId || String(s.id) === String(filterStoreId);
    const oz = (stores || []).filter(
      (s) => s.platform === "ozon" && s.isActive !== false && byId(s)
    );
    const wb = (stores || []).filter(
      (s) => s.platform === "wildberries" && s.isActive !== false && byId(s)
    );
    const ya = (stores || []).filter(
      (s) => s.platform === "yandex" && s.isActive !== false && byId(s)
    );
    storesRef.current = { ozon: oz, wb, ya };
    for (const s of oz)
      ozonStoreStateRef.current[s.id] = ozonStoreStateRef.current[s.id] || {
        lastId: null,
        hasNext: true,
      };
    for (const s of wb)
      wbStoreStateRef.current[s.id] = wbStoreStateRef.current[s.id] || {
        skip: 0,
        take: platformPageSize,
        hasMore: true,
      };
    for (const s of ya)
      yaStoreStateRef.current[s.id] = yaStoreStateRef.current[s.id] || {
        dateTimeTo: null,
        hasMore: true,
      };
  }, [authHeaders, filterStoreId, platformPageSize]);

  const fetchOzonPage = useCallback(async (): Promise<Review[]> => {
    await initStoresIfNeeded();
    const url = getApiUrl(API_CONFIG.ENDPOINTS.OZON_REVIEWS);
    const combined: Review[] = [];
    let anyHasNext = false;
    for (const s of storesRef.current.ozon) {
      const st = ozonStoreStateRef.current[s.id] || {
        lastId: null,
        hasNext: true,
      };
      if (!st.hasNext) continue;
      const params: Record<string, unknown> = {
        limit: platformPageSize,
        storeId: s.id,
      };
      if (st.lastId) params.last_id = st.lastId;
      const { data } = await axios.get<OzonReviewsResponse>(url, {
        params,
        headers: authHeaders,
      });
      const mapped = (data.reviews || [])
        .map((r) => {
          const m = mapOzonToShared(r);
          m.storeId = s.id;
          m.storeName = s.name;
          return m;
        })
        .filter((r) => parseDateToMs(r.published_at) !== -Infinity);
      combined.push(...mapped);
      ozonStoreStateRef.current[s.id] = {
        lastId: (data as any).last_id ?? st.lastId,
        hasNext: Boolean((data as any).has_next),
      };
      anyHasNext = anyHasNext || Boolean((data as any).has_next);
    }
    combined.sort(
      (a, b) => parseDateToMs(b.published_at) - parseDateToMs(a.published_at)
    );
    ozonRef.current.hasNext = anyHasNext;
    return combined;
  }, [authHeaders, initStoresIfNeeded, platformPageSize]);

  const fetchWildberriesPage = useCallback(async (): Promise<Review[]> => {
    await initStoresIfNeeded();
    const url = getApiUrl(API_CONFIG.ENDPOINTS.WILDBERRIES_REVIEWS);
    const combined: Review[] = [];
    let anyHasMore = false;
    for (const s of storesRef.current.wb) {
      const st = wbStoreStateRef.current[s.id] || {
        skip: 0,
        take: platformPageSize,
        hasMore: true,
      };
      if (!st.hasMore) continue;
      const params = {
        take: st.take,
        skip: st.skip,
        order: "dateDesc",
        storeId: s.id,
      };
      const { data } = await axios.get<WildberriesReviewsResponse>(url, {
        params,
        headers: authHeaders,
      });
      const feedbacks = (data as any)?.data?.feedbacks || [];
      const mapped = feedbacks
        .map((f: any) => {
          const m = mapWildberriesToShared(f);
          m.storeId = s.id;
          m.storeName = s.name;
          return m;
        })
        .filter((r: Review) => parseDateToMs(r.published_at) !== -Infinity);
      combined.push(...mapped);
      wbStoreStateRef.current[s.id] = {
        skip: st.skip,
        take: st.take,
        hasMore: feedbacks.length >= st.take,
      };
      anyHasMore = anyHasMore || feedbacks.length >= st.take;
    }
    combined.sort(
      (a, b) => parseDateToMs(b.published_at) - parseDateToMs(a.published_at)
    );
    wbRef.current.hasMore = anyHasMore;
    wbRef.current.lastFetched = combined;
    return combined;
  }, [authHeaders, initStoresIfNeeded, platformPageSize]);

  const ensureBuffers = useCallback(async () => {
    if (ozonRef.current.hasNext) ozonRef.current.buffer = await fetchOzonPage();
    if (wbRef.current.hasMore)
      wbRef.current.buffer = await fetchWildberriesPage();
  }, [fetchOzonPage, fetchWildberriesPage]);

  const fetchYandexForStore = useCallback(
    async (s: StoreConnection): Promise<Review[]> => {
      const st = yaStoreStateRef.current[s.id] || {
        dateTimeTo: null,
        hasMore: true,
      };
      if (!st.hasMore) return [];
      const yaUrl = getApiUrl(API_CONFIG.ENDPOINTS.YANDEX_REVIEWS);
      const params: Record<string, unknown> = {
        limit: platformPageSize,
        storeId: s.id,
      };
      if (st.dateTimeTo) params.dateTimeTo = st.dateTimeTo;
      try {
        const resp = await axios.get<{ reviews?: YandexReviewRaw[] }>(yaUrl, {
          params,
          headers: authHeaders,
        });
        const feedbacks = (resp.data?.reviews || []) as YandexReviewRaw[];
        const list = feedbacks
          .map((f) => {
            const sellerAnswerText =
              (f as any).answer !== undefined && (f as any).answer !== null
                ? (f as any).answer
                : (((f as any).sellerAnswer as any) ?? null);
            const isAnswered =
              typeof sellerAnswerText === "string"
                ? sellerAnswerText.trim().length > 0
                : Boolean(sellerAnswerText);
            const mapped: Review = {
              comments_amount: 0,
              id: String(
                f.feedbackId ??
                  f.id ??
                  `${s.id}:${f.createdAt || Math.random()}`
              ),
              isAnalyzed: Boolean((f as any).isAnalyzed) || false,
              isAnsweredBySeller:
                ((f as any).isAnsweredBySeller as any) ?? isAnswered,
              isFallback: Boolean((f as any).isFallback) || false,
              isResponded:
                ((f as any).isResponded as any) ?? (isAnswered ? true : false),
              is_rating_participant: false,
              offer_id: String(f.identifiers?.modelId ?? ""),
              order_status: "",
              photos_amount: Array.isArray((f as any).media?.photos)
                ? (f as any).media!.photos.length
                : 0,
              platform: "yandex",
              problems: ((f as any).problems ?? null) as string | null,
              product: createPlaceholderProduct({
                id: String(f.identifiers?.modelId ?? ""),
              }),
              published_at: f.createdAt || "",
              rating: Number(f.statistics?.rating ?? 0),
              recommendations: ((f as any).recommendations ?? null) as
                | string
                | null,
              responseTemplate: ((f as any).responseTemplate ?? null) as
                | string
                | null,
              sellerAnswer: (sellerAnswerText as any) ?? null,
              sentiment: ((f as any).sentiment ?? null) as string | null,
              sentimentAnalysis: ((f as any).sentimentAnalysis ?? null) as
                | string
                | null,
              sku: Number(f.identifiers?.modelId ?? 0) || 0,
              status: "",
              text: [
                (f as any).description?.advantages,
                (f as any).description?.comment,
              ]
                .filter(Boolean)
                .join(" \n"),
              videos_amount: Array.isArray((f as any).media?.videos)
                ? (f as any).media!.videos.length
                : 0,
            };
            (mapped as any).storeId = s.id;
            (mapped as any).storeName = s.name;
            (mapped as any).author =
              (f as any).author ||
              (f as any).authorName ||
              (f as any).userName ||
              "";
            (mapped as any).yandex = {
              advantages: (f as any).description?.advantages || "",
              disadvantages: (f as any).description?.disadvantages || "",
              comment: (f as any).description?.comment || "",
              author:
                (f as any).author ||
                (f as any).authorName ||
                (f as any).userName ||
                "",
            };
            return mapped;
          })
          .filter((r) => parseDateToMs(r.published_at) !== -Infinity);
        if (list.length < platformPageSize) {
          yaStoreStateRef.current[s.id] = { ...st, hasMore: false };
        }
        return list;
      } catch {
        yaStoreStateRef.current[s.id] = { ...st, hasMore: false };
        return [];
      }
    },
    [authHeaders, platformPageSize]
  );

  const mergeNextPage = useCallback(async () => {
    await ensureBuffers();
    const pageItems: Review[] = [];
    const wbShownByStore: Record<string, number> = {};
    const yaShownByStore: Record<string, number> = {};

    while (pageItems.length < PAGE_SIZE) {
      const ozonHead = ozonRef.current.buffer[0];
      const wbHead = wbRef.current.buffer[0];

      // Ensure Yandex heads
      const yaHeads: Array<{ storeId: string; review: Review }> = [];
      for (const s of storesRef.current.ya) {
        const key = `__ya_buffer_${s.id}` as const;
        const anyGlobal = yaStoreStateRef as any;
        if (!anyGlobal[key] || (anyGlobal[key] as Review[]).length === 0) {
          const list = await fetchYandexForStore(s);
          anyGlobal[key] = list;
        }
        const buf: Review[] = (anyGlobal[key] || []) as Review[];
        if (buf[0]) yaHeads.push({ storeId: s.id, review: buf[0] });
      }
      const yaHead = yaHeads.sort(
        (a, b) =>
          parseDateToMs(b.review.published_at) -
          parseDateToMs(a.review.published_at)
      )[0]?.review;

      if (!ozonHead && !wbHead && !yaHead) {
        const fetched: boolean[] = [false, false, false];
        if (ozonRef.current.hasNext) {
          const moreO = await fetchOzonPage();
          ozonRef.current.buffer = moreO;
          fetched[0] = moreO.length > 0;
        }
        if (wbRef.current.hasMore) {
          const moreW = await fetchWildberriesPage();
          wbRef.current.buffer = moreW;
          fetched[1] = moreW.length > 0;
        }
        let anyYaFetched = false;
        for (const s of storesRef.current.ya) {
          const key = `__ya_buffer_${s.id}` as const;
          const list = await fetchYandexForStore(s);
          (yaStoreStateRef as any)[key] = list;
          anyYaFetched = anyYaFetched || list.length > 0;
        }
        fetched[2] = anyYaFetched;
        if (!fetched[0] && !fetched[1]) break;
        continue;
      }

      const ozonTime = ozonHead
        ? parseDateToMs(ozonHead.published_at)
        : -Infinity;
      const wbTime = wbHead ? parseDateToMs(wbHead.published_at) : -Infinity;
      const yaTime = yaHead ? parseDateToMs(yaHead.published_at) : -Infinity;
      if (ozonTime >= wbTime && ozonTime >= yaTime && ozonHead) {
        pageItems.push(ozonRef.current.buffer.shift() as Review);
      } else if (wbTime >= ozonTime && wbTime >= yaTime && wbHead) {
        const taken = wbRef.current.buffer.shift() as Review;
        pageItems.push(taken);
        if (taken.storeId)
          wbShownByStore[taken.storeId] =
            (wbShownByStore[taken.storeId] || 0) + 1;
      } else if (yaHead) {
        for (const s of storesRef.current.ya) {
          const key = `__ya_buffer_${s.id}` as const;
          const buf: Review[] = (yaStoreStateRef as any)[key] || [];
          if (buf[0] && buf[0].id === yaHead.id) {
            const taken = (yaStoreStateRef as any)[key].shift() as Review;
            pageItems.push(taken);
            if (taken.storeId)
              yaShownByStore[taken.storeId] =
                (yaShownByStore[taken.storeId] || 0) + 1;
            break;
          }
        }
      }
    }

    // Boundary check: ensure cross-platform order stability
    const finalizeWithBoundaryCheck = (items: Review[]): Review[] => {
      if (items.length === 0) return items;
      const last = items[items.length - 1];
      const lastPlatform = last.platform;
      const boundaryTime = parseDateToMs(last.published_at);
      const findNextCandidateTimeForPlatform = (
        platform: string
      ): number | null => {
        if (platform === "wildberries") {
          for (let i = items.length - 1; i >= 0; i -= 1) {
            if (items[i].platform === "ozon") {
              const candidate = ozonRef.current.buffer[0];
              return candidate ? parseDateToMs(candidate.published_at) : null;
            }
          }
          return null;
        } else {
          for (let i = items.length - 1; i >= 0; i -= 1) {
            if (items[i].platform === "wildberries") {
              const candidate = wbRef.current.buffer[0];
              return candidate ? parseDateToMs(candidate.published_at) : null;
            }
          }
          return null;
        }
      };
      const nextOtherTime = findNextCandidateTimeForPlatform(lastPlatform);
      if (nextOtherTime !== null && nextOtherTime > boundaryTime) {
        return items.slice(0, items.length - 1);
      }
      return items;
    };

    const checked = finalizeWithBoundaryCheck(pageItems);

    const lastPerOzonStore: Record<string, string> = {};
    for (let i = checked.length - 1; i >= 0; i -= 1) {
      const r = checked[i];
      if (r.platform === "ozon" && r.storeId && !lastPerOzonStore[r.storeId]) {
        lastPerOzonStore[r.storeId] = r.id;
      }
    }
    for (const storeId of Object.keys(lastPerOzonStore)) {
      if (ozonStoreStateRef.current[storeId]) {
        ozonStoreStateRef.current[storeId].lastId = lastPerOzonStore[storeId];
      }
    }
    for (const [storeId, shownCount] of Object.entries(wbShownByStore)) {
      if (wbStoreStateRef.current[storeId])
        wbStoreStateRef.current[storeId].skip += shownCount;
    }
    const lastPerYaStore: Record<string, string> = {};
    for (let i = checked.length - 1; i >= 0; i -= 1) {
      const r = checked[i];
      if (r.platform === "yandex" && r.storeId && !lastPerYaStore[r.storeId]) {
        lastPerYaStore[r.storeId] = String(r.published_at);
      }
    }
    for (const [storeId, dt] of Object.entries(lastPerYaStore)) {
      if (yaStoreStateRef.current[storeId])
        yaStoreStateRef.current[storeId].dateTimeTo = dt || null;
    }
    // Clear buffers to ensure new data comes on next page
    ozonRef.current.buffer = [];
    wbRef.current.buffer = [];
    for (const s of storesRef.current.ya) {
      const key = `__ya_buffer_${s.id}` as const;
      (yaStoreStateRef as any)[key] = [];
    }
    const anyYaHasMore = storesRef.current.ya.some(
      (s) => yaStoreStateRef.current[s.id]?.hasMore !== false
    );
    const more =
      ozonRef.current.hasNext || wbRef.current.hasMore || anyYaHasMore;

    return { items: checked, more } as { items: Review[]; more: boolean };
  }, [ensureBuffers, fetchOzonPage, fetchWildberriesPage, fetchYandexForStore]);

  const fetchNextPage = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const merged = (await mergeNextPage()) as unknown as {
        items: Review[];
        more: boolean;
      } | null;
      if (!merged) {
        setHasMore(false);
        return;
      }
      setReviews((prev) =>
        prev.length === 0 ? merged.items : [...prev, ...merged.items]
      );
      setHasMore(merged.more);
    } catch {
      setError("Ошибка при загрузке отзывов");
    } finally {
      setLoading(false);
    }
  }, [mergeNextPage]);

  const refresh = useCallback(async () => {
    // reset cursors and state
    ozonRef.current = { lastId: null, hasNext: true, buffer: [] };
    wbRef.current = {
      skip: 0,
      take: platformPageSize,
      hasMore: true,
      buffer: [],
      lastFetched: [],
    };
    ozonStoreStateRef.current = {};
    wbStoreStateRef.current = {} as any;
    yaStoreStateRef.current = {} as any;
    storesRef.current = { ozon: [], wb: [], ya: [] };
    setReviews([]);
    setHasMore(true);
    await fetchNextPage();
  }, [fetchNextPage, platformPageSize]);

  const updateReviews = useCallback(
    (updater: React.SetStateAction<Review[]>) => {
      setReviews(updater);
    },
    []
  );

  return {
    reviews,
    loading,
    hasMore,
    error,
    fetchNextPage,
    refresh,
    updateReviews,
  };
};
